import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_ROUTES, API_DOMAIN, headers } from '../../Reusable_Components/Config/apiConfig';
import LoadingSkeleton from '../../Reusable_Components/LoadingSkeleton';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const rawQuery = searchParams.get('q') || '';
  const query = rawQuery.trim();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({
    contacts: [],
    leads: [],
    customers: [],
    opportunities: [],
    tasks: [],
    quotes: [],
    workflows: [],
  });
  const [activeTab, setActiveTab] = useState('all');
  const debounceTimer = useRef(null);

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults({
        contacts: [],
        leads: [],
        customers: [],
        opportunities: [],
        tasks: [],
        quotes: [],
        workflows: [],
      });
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      const fetchAllData = async () => {
      setLoading(true);
      try {
        const [contactsRes, leadsRes, customersRes, opportunitiesRes, tasksRes, quotesRes, workflowsRes] = await Promise.all([
          axios.get(`${API_DOMAIN}${API_ROUTES.contact}`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_DOMAIN}${API_ROUTES.lead}`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_DOMAIN}${API_ROUTES.customer}`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_DOMAIN}${API_ROUTES.opportunity}`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_DOMAIN}${API_ROUTES.task}`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_DOMAIN}${API_ROUTES.quote}`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_DOMAIN}${API_ROUTES.workflow}`, { headers }).catch(() => ({ data: [] })),
        ]);

        const q = query.toLowerCase();

        // Backend list endpoints return a paginated {count,next,previous,results}
        // envelope, not a flat array — unwrap before filtering.
        const asArray = (raw) => Array.isArray(raw) ? raw : (Array.isArray(raw?.results) ? raw.results : []);

        // Filter Contacts
        const matchedContacts = asArray(contactsRes.data).filter(c =>
          String(c.first_name || '').toLowerCase().includes(q) ||
          String(c.last_name || '').toLowerCase().includes(q) ||
          String(c.email || '').toLowerCase().includes(q) ||
          String(c.phone_number || '').toLowerCase().includes(q)
        );

        // Filter Leads
        const matchedLeads = asArray(leadsRes.data).filter(l =>
          String(l.lead_source || '').toLowerCase().includes(q) ||
          String(l.status || '').toLowerCase().includes(q) ||
          String(l.lead_score || '').toLowerCase().includes(q)
        );

        // Filter Customers
        const matchedCustomers = asArray(customersRes.data).filter(c =>
          String(c.customer_type || '').toLowerCase().includes(q) ||
          String(c.status || '').toLowerCase().includes(q)
        );

        // Filter Opportunities
        const matchedOpportunities = asArray(opportunitiesRes.data).filter(o =>
          String(o.deal_stage || '').toLowerCase().includes(q) ||
          String(o.probability || '').toLowerCase().includes(q) ||
          String(o.amount || '').toLowerCase().includes(q)
        );

        // Filter Tasks
        const matchedTasks = asArray(tasksRes.data).filter(t =>
          String(t.title || '').toLowerCase().includes(q) ||
          String(t.status || '').toLowerCase().includes(q) ||
          String(t.priority || '').toLowerCase().includes(q)
        );

        // Filter Quotes
        const matchedQuotes = asArray(quotesRes.data).filter(q_item =>
          String(q_item.quote_number || '').toLowerCase().includes(q) ||
          String(q_item.customer?.name || '').toLowerCase().includes(q) ||
          String(q_item.status?.name || '').toLowerCase().includes(q)
        );

        // Filter Workflows
        const matchedWorkflows = asArray(workflowsRes.data).filter(w =>
          String(w.workflow_name || '').toLowerCase().includes(q) ||
          String(w.trigger || '').toLowerCase().includes(q) ||
          String(w.conditions || '').toLowerCase().includes(q) ||
          String(w.actions || '').toLowerCase().includes(q)
        );

        setResults({
          contacts: matchedContacts.slice(0, 50),
          leads: matchedLeads.slice(0, 50),
          customers: matchedCustomers.slice(0, 50),
          opportunities: matchedOpportunities.slice(0, 50),
          tasks: matchedTasks.slice(0, 50),
          quotes: matchedQuotes.slice(0, 50),
          workflows: matchedWorkflows.slice(0, 50),
        });
      } catch (err) {
        console.error('Error searching data:', err);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchAllData();
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [query]);

  const totalResults =
    results.contacts.length +
    results.leads.length +
    results.customers.length +
    results.opportunities.length +
    results.tasks.length +
    results.quotes.length +
    results.workflows.length;

  if (!query) {
    return (
      <div className="search-empty-container">
        <p className="search-info-text">Please enter a search query in the search bar above.</p>
      </div>
    );
  }

  if (query.length < 2) {
    return (
      <div className="search-empty-container">
        <p className="search-info-text">Type at least 2 characters to search.</p>
      </div>
    );
  }

  return (
    <div className="search-results-page">
      <div className="search-results-meta">
        Found {loading ? '...' : totalResults} results for "{query}"
      </div>

      {/* Tabs */}
      <div className="search-tabs">
        <button
          className={`search-tab-btn${activeTab === 'all' ? ' active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All ({totalResults})
        </button>
        <button
          className={`search-tab-btn${activeTab === 'contacts' ? ' active' : ''}`}
          onClick={() => setActiveTab('contacts')}
        >
          Contacts ({results.contacts.length})
        </button>
        <button
          className={`search-tab-btn${activeTab === 'leads' ? ' active' : ''}`}
          onClick={() => setActiveTab('leads')}
        >
          Leads ({results.leads.length})
        </button>
        <button
          className={`search-tab-btn${activeTab === 'customers' ? ' active' : ''}`}
          onClick={() => setActiveTab('customers')}
        >
          Customers ({results.customers.length})
        </button>
        <button
          className={`search-tab-btn${activeTab === 'opportunities' ? ' active' : ''}`}
          onClick={() => setActiveTab('opportunities')}
        >
          Opportunities ({results.opportunities.length})
        </button>
        <button
          className={`search-tab-btn${activeTab === 'tasks' ? ' active' : ''}`}
          onClick={() => setActiveTab('tasks')}
        >
          Tasks ({results.tasks.length})
        </button>
        <button
          className={`search-tab-btn${activeTab === 'quotes' ? ' active' : ''}`}
          onClick={() => setActiveTab('quotes')}
        >
          Quotes ({results.quotes.length})
        </button>
        <button
          className={`search-tab-btn${activeTab === 'workflows' ? ' active' : ''}`}
          onClick={() => setActiveTab('workflows')}
        >
          Workflows ({results.workflows.length})
        </button>
      </div>

      {loading ? (
        <div style={{ marginTop: 20 }}>
          <LoadingSkeleton />
        </div>
      ) : (
        <div className="search-results-list">
          {/* Contacts Section */}
          {(activeTab === 'all' || activeTab === 'contacts') && results.contacts.length > 0 && (
            <div className="search-category-section">
              <h2 className="search-category-title">Contacts</h2>
              <div className="search-grid">
                {results.contacts.map(c => (
                  <div key={c.id} className="search-result-card">
                    <div className="card-primary-info">{c.first_name} {c.last_name}</div>
                    <div className="card-secondary-info">📧 {c.email || 'No email'} | 📞 {c.phone_number || 'No phone'}</div>
                    <Link to={`/contact/details/${btoa(c.id)}`} className="btn btn-secondary btn-sm" style={{ marginTop: 10 }}>
                      View Details
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Leads Section */}
          {(activeTab === 'all' || activeTab === 'leads') && results.leads.length > 0 && (
            <div className="search-category-section">
              <h2 className="search-category-title">Leads</h2>
              <div className="search-grid">
                {results.leads.map(l => (
                  <div key={l.id} className="search-result-card">
                    <div className="card-primary-info">Lead Source: {l.lead_source}</div>
                    <div className="card-secondary-info">Status: {l.status} | Score: {l.lead_score}</div>
                    <Link to={`/lead/details/${btoa(l.id)}`} className="btn btn-secondary btn-sm" style={{ marginTop: 10 }}>
                      View Details
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Customers Section */}
          {(activeTab === 'all' || activeTab === 'customers') && results.customers.length > 0 && (
            <div className="search-category-section">
              <h2 className="search-category-title">Customers</h2>
              <div className="search-grid">
                {results.customers.map(c => (
                  <div key={c.id} className="search-result-card">
                    <div className="card-primary-info">Customer Type: {c.customer_type}</div>
                    <div className="card-secondary-info">Status: {c.status}</div>
                    <Link to={`/customer/details/${btoa(c.id)}`} className="btn btn-secondary btn-sm" style={{ marginTop: 10 }}>
                      View Details
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Opportunities Section */}
          {(activeTab === 'all' || activeTab === 'opportunities') && results.opportunities.length > 0 && (
            <div className="search-category-section">
              <h2 className="search-category-title">Opportunities</h2>
              <div className="search-grid">
                {results.opportunities.map(o => (
                  <div key={o.id} className="search-result-card">
                    <div className="card-primary-info">Stage: {o.deal_stage}</div>
                    <div className="card-secondary-info">Amount: KES {Number(o.amount).toLocaleString()} | Probability: {o.probability}%</div>
                    <Link to={`/opportunity/details/${btoa(o.id)}`} className="btn btn-secondary btn-sm" style={{ marginTop: 10 }}>
                      View Details
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tasks Section */}
          {(activeTab === 'all' || activeTab === 'tasks') && results.tasks.length > 0 && (
            <div className="search-category-section">
              <h2 className="search-category-title">Tasks</h2>
              <div className="search-grid">
                {results.tasks.map(t => (
                  <div key={t.id} className="search-result-card">
                    <div className="card-primary-info">{t.title}</div>
                    <div className="card-secondary-info">Status: {t.status} | Priority: {t.priority}</div>
                    <Link to={`/task/details/${btoa(t.id)}`} className="btn btn-secondary btn-sm" style={{ marginTop: 10 }}>
                      View Details
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quotes Section */}
          {(activeTab === 'all' || activeTab === 'quotes') && results.quotes.length > 0 && (
            <div className="search-category-section">
              <h2 className="search-category-title">Quotes</h2>
              <div className="search-grid">
                {results.quotes.map(q_item => (
                  <div key={q_item.id} className="search-result-card">
                    <div className="card-primary-info">Quote: {q_item.quote_number}</div>
                    <div className="card-secondary-info">Customer: {q_item.customer?.name || 'Unknown'} | Status: {q_item.status?.name || 'Draft'}</div>
                    <Link to={`/quote/details/${btoa(q_item.id)}`} className="btn btn-secondary btn-sm" style={{ marginTop: 10 }}>
                      View Details
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Workflows Section */}
          {(activeTab === 'all' || activeTab === 'workflows') && results.workflows.length > 0 && (
            <div className="search-category-section">
              <h2 className="search-category-title">Workflows</h2>
              <div className="search-grid">
                {results.workflows.map(w => (
                  <div key={w.id} className="search-result-card">
                    <div className="card-primary-info">{w.workflow_name}</div>
                    <div className="card-secondary-info">Trigger: {w.trigger} | Actions: {w.actions}</div>
                    <Link to={`/workflow/details/${btoa(w.id)}`} className="btn btn-secondary btn-sm" style={{ marginTop: 10 }}>
                      View Details
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!loading && totalResults === 0 && (
            <div className="search-empty-container">
              <p className="search-info-text">No matching records found for "{query}".</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchResults;
